import { queryOptions } from "@tanstack/react-query";
import type { WorkspaceRoleService } from "@/services/workspace-role.service";
import type { TRoleEligibility } from "@/types/role-eligibility";
import type { ICustomRole } from "@/types/workspace";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The workspace role reads (NG-2814):
 *
 * - `roles.list`: every role of the workspace, system and custom,
 *   `[slug, uid, 'roles', 'list']`. Mutable: the roles page is where admins
 *   edit them, and the members screens read it for role names and tiers.
 * - `roles.catalogue`: the permission catalogue for one tier,
 *   `[slug, uid, 'roles', 'catalogue', tier]`. Reference: it is the product's
 *   permission list, split into available and locked against the tier, so it
 *   is keyed by tier (one held for the wrong tier would offer a Guest role the
 *   Administrator toggles, NG-1272).
 * - `role.draft`: one role's unpublished snapshot, the role editor's autosave
 *   baseline, `[slug, uid, 'role', id, 'draft']`. Access-sensitive, and read
 *   again on every open of the editor whatever its age (`refetchOnMount:
 *   "always"`), since an editor that starts from an old baseline would
 *   autosave it back over another admin's edit.
 *
 * - `role.eligibility`: who one role's holders may be assigned as, the editor's
 *   "Can be assigned as" section (NG-3896), `[slug, uid, 'role', id,
 *   'eligibility']`. Access-sensitive: it decides who a Lead picker offers.
 *
 * All four are plain GETs, so all declare `"safe-read"`.
 *
 * @writers `WorkspaceRolesStore`: create, duplicate, publish and delete patch
 * the list and then invalidate it, because `is_default` and `can_delete` are
 * verdicts about the whole workspace that one row's answer cannot carry
 * (NG-1335); set-default patches every row from its answer. The draft is
 * written only by its editor, which keeps each autosave's answer as its own
 * baseline and reads the draft again on its next open. The eligibility is
 * written by `useRoleEligibility`'s `save`, which sets the answer it sends at
 * once and then the server's.
 */

/** The roles list key. */
export function workspaceRolesKey(scope: QueryScope) {
  return scopedKey(scope, "roles", "list");
}

/** The permission catalogue key for `tier`. */
function permissionCatalogueKey(scope: QueryScope, tier: string) {
  return scopedKey(scope, "roles", "catalogue", tier);
}

/** One role's draft key. */
function roleDraftKey(scope: QueryScope, roleId: string) {
  return scopedKey(scope, "role", roleId, "draft");
}

/** Every role of the workspace. */
export function workspaceRolesQuery(
  service: Pick<WorkspaceRoleService, "listRoles">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: workspaceRolesKey(scope),
    queryFn: scopedRead((request) => service.listRoles(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** The permission catalogue for `tier`. */
export function permissionCatalogueQuery(
  service: Pick<WorkspaceRoleService, "getPermissionCatalogue">,
  scope: QueryScope,
  tier: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: permissionCatalogueKey(scope, tier),
    queryFn: scopedRead((request) => service.getPermissionCatalogue(scope.tenant, tier, request), {
      retry: "safe-read",
    }),
  });
}

/** One role's unpublished draft, read when its editor opens. */
export function roleDraftQuery(
  service: Pick<WorkspaceRoleService, "getRoleDraft">,
  scope: QueryScope,
  roleId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    // The class reuses an answer for 60 s (NG-3012); an editor baseline may not
    // be reused at all, and `useRoleDraft` waits for this mount's own read.
    refetchOnMount: "always",
    queryKey: roleDraftKey(scope, roleId),
    queryFn: scopedRead((request) => service.getRoleDraft(scope.tenant, roleId, request), {
      retry: "safe-read",
    }),
  });
}

/** One role's eligibility key. */
export function roleEligibilityKey(scope: QueryScope, roleId: string) {
  return scopedKey(scope, "role", roleId, "eligibility");
}

/** Who one role's holders may be assigned as (NG-3896). */
export function roleEligibilityQuery(
  service: Pick<WorkspaceRoleService, "getRoleEligibility">,
  scope: QueryScope,
  roleId: string,
) {
  return queryOptions<TRoleEligibility>({
    ...FRESHNESS["access-sensitive"],
    queryKey: roleEligibilityKey(scope, roleId),
    queryFn: scopedRead((request) => service.getRoleEligibility(scope.tenant, roleId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * The roles list for a route loader: a fresh entry answers with no request,
 * and the answer lands where every `useWorkspaceRoles` reader sees it. Before
 * the session resolves it reads straight from the service, uncached.
 */
export function readWorkspaceRoles(
  service: Pick<WorkspaceRoleService, "listRoles">,
  slug: string,
): Promise<ICustomRole[]> {
  const scope = scopeIfReady(slug, "workspace roles");
  if (!scope) return service.listRoles(slug);
  return getQueryClient().fetchQuery(workspaceRolesQuery(service, scope));
}
